<script lang="ts" setup>
/**
 * Das ist eine Beta View mit der wir ausschließlich Client/Project + Note erfassen.
 * Das soll testweiste mal mit ein paar Leuten aus dem Kundenteam getestet werden um
 * herauszufinden, ob diese Art von Erfassung besser zu Sunlab passt.
 */

import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { Client } from '@/models/Client';
import { useMainStore } from '@/store/main';
import { Project } from '@/models/Project';
import { FirstLevelOption, fromClientAndMaybeProject } from '@/models/FirstLevelOption';
import { useClientsStore } from '@/store/clients';
import { useProjectsStore } from '@/store/projects';
import FirstLevelSelectInput from '@/components/FirstLevelSelectInput.vue';
import { BeatMeta, groupBeatsByTopicResolved, isBeatValid, resolveBeat } from '@/models/Beats';
import { useBeatsStore } from '@/store/beats';
import AppButton from '@/components/AppButton.vue';
import { useActivitiesStore } from '@/store/activities';

const mainStore = useMainStore();
const beatsStore = useBeatsStore();
const clientsStore = useClientsStore();
const projectsStore = useProjectsStore();
const activitiesStore = useActivitiesStore();

const selectedClient = ref<Client | null>(null);
const selectedProject = ref<Project | null>(null);
const note = ref('');

const selectedFirstLevelOption = computed<FirstLevelOption | null>({
	get() {
		if (!selectedClient.value) {
			return null;
		}

		return fromClientAndMaybeProject(selectedClient.value, selectedProject.value);
	},
	set(option) {
		selectedClient.value = option ? option.client : null;
		selectedProject.value = option ? option.project : null;
	},
});

const toast = useToast();

const { t } = useI18n();

const onSubmit = async () => {
	const beat: Partial<BeatMeta> = {
		activityId: import.meta.env.VITE_NOTES_ONLY_ACTIVITY_ID,
		clientId: selectedClient.value?.id,
		note: note.value,
		satisfaction: null,
		sharedBeatId: null,
		timeIntervalInMs: 900_000,
		timestamp: DateTime.now(),
		userId: mainStore.user?.id,
		projectId: selectedProject.value?.id,
	};

	if (!isBeatValid(beat)) {
		toast.error(t('notifications.warningLogIsNotValid'));
		return;
	}

	const result = await beatsStore.createBeat(beat);

	if (result.isErr()) {
		toast.error(t('notifications.errorSomethingWentWrong'));
		return;
	}

	selectedClient.value = null;
	selectedProject.value = null;
	note.value = '';

	toast.success(t('notifications.successCreateLog'));
};

const textareaHeight = ref('auto');

const handleTextareaHeight = (event: Event) => {
	textareaHeight.value = 'auto';
	const target = event.target as HTMLInputElement;
	textareaHeight.value = `${target.scrollHeight}px`;
};

const isValid = computed(() => {
	return note.value.length > 0 && selectedClient.value !== null;
});

const logsFromToday = computed(() => beatsStore.beatsFromToday);

const logsResolved = computed(() => logsFromToday.value.map((beat) => resolveBeat({ beat, activities: activitiesStore.activities, clients: clientsStore.clients, projects: projectsStore.projects })));

const logsGroupedByClients = computed(() => groupBeatsByTopicResolved({
	beats: logsResolved.value,
	activities: activitiesStore.activities,
	clients: clientsStore.clients,
	projects: projectsStore.projects,
}));
</script>

<template>
	<div>
		<div class="w-full max-w-xl mx-auto min-h-[80vh] flex items-center justify-center">
			<div class="w-full bg-primary-400 phablet:rounded-2xl p-6 phablet:p-12 space-y-8 shadow-widget">
				<div class="flex items-start font-semibold text-[20px] phablet:text-[24px]">
					<div>
						{{ $t('dashboard.salutation', { name: mainStore.user?.name.split(' ')[0] }) }}
					</div>
				</div>
				<FirstLevelSelectInput
					id="FirstLevelSelectInput"
					ref="firstInputRef"
					v-model="selectedFirstLevelOption"
					:project-options="projectsStore.projects"
					:client-options="clientsStore.clients"
					:placeholder="$t('dashboard.firstLevelSelectInput')"
					class="text-white text-opacity-100 desktop:col-span-4"
				/>
				<div class="relative">
					<textarea
						v-model="note"
						class="text-white bg-primary-300 rounded-md w-full px-4 pt-4 pb-14 text-base border border-primary-900 focus:outline-none focus-visible:outline-none focus:border-white resize-none overflow-hidden min-h-32"
						placeholder="Was genau hast du für den Kunden oder das Projekt gemacht?"
						aria-label="note"
						:style="{ height: textareaHeight }"
						@input="handleTextareaHeight"
					/>
					<AppButton
						size="sm"
						class="!absolute bottom-5 right-3"
						:disabled="!isValid"
						@click="onSubmit"
					>
						Loggen
					</AppButton>
				</div>
			</div>
		</div>
		<div class="flex items-start font-semibold text-[20px] phablet:text-[24px] mb-6">
			<div
				class="w-[20px] phablet:w-12 mt-2.5 phablet:mt-3.5 bg-primary-100 h-1 rounded-full mr-4"
			/>
			<div>
				Was ist heute so passiert?
			</div>
		</div>
		<div class="space-y-4">
			<div
				v-for="(beats, key) in logsGroupedByClients"
				:key="key"
			>
				<div
					v-for="(beat, $index) in beats"
					:key="beat.beatId"
				>
					<p
						v-if="$index === 0"
						class="text-lg font-bold"
					>
						{{ beat.client }}
						<span v-if="beat.projectId"> – {{ beat.project }}</span>
					</p>
					<p>- {{ beat.note }}</p>
				</div>
			</div>
		</div>
	</div>
</template>
